import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, ExternalLink } from "lucide-react";

export default function CardsPage() {
  const [cards, setCards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get<{ cards: any[]; total: number }>("/api/v1/cards")
      .then((data) => setCards(data.cards || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (uid: number) => {
    if (!confirm("Delete this card?")) return;
    try {
      await api.delete(`/api/v1/cards/${uid}`);
      setCards(cards.filter((c) => c.uid !== uid));
      toast.success("Card deleted");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const filtered = cards.filter((c) => {
    const q = search.toLowerCase();
    return !q || (c.personal_id || "").toLowerCase().includes(q) || (c.name_en || "").toLowerCase().includes(q) || (c.name_tc || "").toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Cards</h1>
        <Link to="/admin/cards/new" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Card
        </Link>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search cards..." className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-input bg-background text-sm" />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-4 animate-pulse">
              <div className="h-5 bg-muted rounded w-40 mb-2" />
              <div className="h-4 bg-muted rounded w-24" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {search ? "No cards match your search." : "No cards yet. Create your first card!"}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((card) => (
            <div key={card.uid} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-semibold text-primary">
                  {(card.name_en || card.personal_id || "?").charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{card.name_en || card.name_tc || card.personal_id}</p>
                <p className="text-xs text-muted-foreground">/{card.personal_id} · {card.status}</p>
              </div>
              <div className="flex items-center gap-1">
                <a href={`/u/${card.personal_id}`} target="_blank" className="p-2 rounded-lg hover:bg-accent text-muted-foreground" title="View public card">
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link to={`/admin/cards/${card.uid}`} className="p-2 rounded-lg hover:bg-accent text-muted-foreground" title="Edit">
                  <Pencil className="w-4 h-4" />
                </Link>
                <button onClick={() => handleDelete(card.uid)} className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
